import { type JSONSchema7TypeName } from "json-schema";

import {
  ManifestJSONSchema,
  JSONSchemaPropertyDefinition,
} from "src/api/connectApi/manifestTypes";

/**
 * Helper function to check if a field's type matches the expected type.
 * Handles all JSON Schema type variations:
 * - Single string: type: "string"
 * - Array with single type: type: ["string"]
 * - Array with multiple types: type: ["string", "null"]
 */
const isTypeMatch = (
  fieldType: JSONSchema7TypeName | JSONSchema7TypeName[] | undefined,
  expectedType: JSONSchema7TypeName,
): boolean => {
  if (!fieldType) {
    return false;
  }

  // Handle single string type
  if (typeof fieldType === "string") {
    return fieldType === expectedType;
  }

  // Handle array of types
  if (Array.isArray(fieldType)) {
    return fieldType.includes(expectedType);
  }

  return false;
};

/**
 * Check if a field is of a specific type, including nullable variants.
 * Handles all type variations:
 * - type: "string"
 * - type: ["string"]
 * - type: ["string", "null"]
 * - type: ["null", "string"]
 */
export const isFieldOfNullableType = (
  field: ManifestJSONSchema,
  type: JSONSchema7TypeName,
): boolean => {
  return isTypeMatch(field.type, type);
};

export const isFieldString = (field: ManifestJSONSchema): boolean => {
  return isFieldOfNullableType(field, "string");
};

export const isFieldNumber = (field: ManifestJSONSchema): boolean => {
  return isFieldOfNullableType(field, "number");
};

export const isFieldInteger = (field: ManifestJSONSchema): boolean => {
  return isFieldOfNullableType(field, "integer");
};

export const isFieldBoolean = (field: ManifestJSONSchema): boolean => {
  return isFieldOfNullableType(field, "boolean");
};

export const isFieldMultiSelect = (
  field: JSONSchemaPropertyDefinition,
): boolean => {
  return field?.widget === "multi_select";
};

export const isFieldTextarea = (
  field: JSONSchemaPropertyDefinition,
): boolean => {
  return field?.widget === "textarea";
};

export const isFieldTagsInput = (
  field: JSONSchemaPropertyDefinition,
): boolean => {
  return field?.widget === "tags_input";
};

/**
 * Check if a field is an array of primitives (not objects or arrays).
 * Handles all type variations for both the field and its items.
 */
export const isFieldPrimitiveArray = (
  field: JSONSchemaPropertyDefinition,
): boolean => {
  if (!isFieldOfNullableType(field, "array")) {
    return false;
  }

  const items = field.items as ManifestJSONSchema;
  if (!items) {
    return false;
  }

  // Check if items type is NOT object or array (i.e., it's a primitive)
  const isObjectType = isTypeMatch(items.type, "object");
  const isArrayType = isTypeMatch(items.type, "array");

  return !isObjectType && !isArrayType;
};
